/**
 * @ngdoc controllers
 * @memberof mercury.modules.time
 * @name TimeRosterWeekviewCtrl
 */
angular.module('mercury.modules.time.roster')
.controller('TimeRosterWeekviewCtrl', ["dialog", "$filter", "reporttime", "$scope", "$stateParams", "logger", "userinfo", function(dialog, $filter, reporttime, $scope, $stateParams, logger, userinfo) {

  $scope.templateId = $stateParams.templateId;
  $scope.unavailability = $scope.unavailability || [];
  $scope.leaveRequests = $scope.leaveRequests || [];

  // Build the days list for the week view, i.e. days from the start of the week
  // we're looking at.
  $scope.days = [];
  for (var i = 0; i < 7; i++) {
    var day = moment($scope.viewStartDateTime).clone().add(i, 'days');
    var startTime = day.format();
    var endTime = day.clone().add(1, 'day').format();
    var link = $scope.isTemplate ? 
      JSON.stringify({start: startTime, end: endTime, type: 'custom', period: 'day'}) : 
      JSON.stringify({time: startTime, type: 'day'})
    
    $scope.days.push({
      startTime: startTime,
      startDate: day.format('YYYY-MM-DD'),
      endTime: endTime,
      link: link,
      name: day.format('dddd'),
      // note this index is _not_ the usual Sunday = 0 index, it is a column
      // position in the ui, and changes based on what the company start day is
      dayIndex: i,
    });
  }

  /**
   * Helper to get the appropriate day details object from the $scope.days array
   * for the given moment.
   */
  function getDayDetails(startMoment) {
    return $scope.days.find(day => moment(day.startTime).format('YYYY-MM-DD') === startMoment.format('YYYY-MM-DD'));
  }

  /**
   * Helper to get the appropriate day details object from the $scope.days array
   * for the given day name.
   */
  function getDayDetailByName(dayName) {
    return $scope.days.find(day => day.name === dayName);
  }

  /**
   * Helper to get the day index (Sunday = 0) of a moment.
   */
  function getDayIndex(aMoment) {
    var index = null;
    for (const dayIndex in $scope.days) {
      var day = $scope.days[dayIndex];
      if (
        aMoment.isSame(day.startTime) ||
        aMoment.isBetween(day.startTime, day.endTime)
      ) {
        index = dayIndex;
      }
    }
    return index;
  }

  /**
   * Helper that given two moments representing the start and end points of
   * a range, will return records from $scope.days that run from the start
   * of the requested range up until the end time, or the end of the week
   * that is being displayed in the UI.
   */
  function getAllDayDetailsWithinRange(startMoment, endMoment) {
    var dayDetails = [];
    var currentDay = startMoment.clone();

    let currentDayDetails = getDayDetails(currentDay)

    while (
      // days are only included if they're in the range specified (exclusive),
      // we only check the day because the current day is started from the
      // startMoment and this may have a different time than the end moment.
      currentDay.isSameOrBefore(endMoment, 'day') &&
      // the last day on the UI has the day index 6, we don't want any days
      // beyond that
      (!currentDayDetails || currentDayDetails.dayIndex < 7) &&
      // we don't want more than a complete week's worth of days
      dayDetails.length < 8
    ) {
      if (currentDayDetails) {
        // a day we care about, add it to the list
        dayDetails.push(currentDayDetails);
      }

      // bump for the next loop iteration
      currentDay.add(1, 'day');
      currentDayDetails = getDayDetails(currentDay)
    }

    return dayDetails;
  }



  /**
   * This function sets the time boundaries for a OneOff unavailability record and leave request tiles
   * @param {*} startMoment - the start moment of the leave or unavailability record
   * @param {*} endMoment - the end moment of the leave or unavailability record
   * @param {*} id - the id of the leave or unavailability time in case of a one off unavailability record
   * @param {*} type - OneOff in case of a one off unavailability record and leave type in case of a leave request
   * @param {*} reason - the reason for the leave request or the reason for the unavailability
   * @param {*} status - the status of the leave or unavailability record
   * @param {*} staffMemberId - the staff member id of the leave or unavailability record
   * @param {*} propertyName - the property name of the unavailability or leave request array
   */
  var setRecordTimeBoundaries = function(startMoment, endMoment, id, type, reason, status, staffMemberId, propertyName) {
    // Only show the unavailability if the date is within the view
    // time range we're displaying.
    //                                     
    //  |          |<-  range  ->|         |
    //  │          │             │         │  
    //  │ ┌───┐  ┌─┴─┐  ┌───┐  ┌─┴─┐ ┌───┐ │  
    //  │ │ ✕ │  │ ✓ │  │ ✓ │  │ ✓ │ │ ✕ │ │  
    //  │ └───┘  └─┬─┘  └───┘  └─┬─┘ └───┘ │  
    //  │        ┌─┴─────────────┴─┐       │  
    //  │        │        ✓        │       │  
    //  │        └─┬─────────────┬─┘       │  
    //  │          │             │         │  
    //
    if (
      // start is within the period
      startMoment.isBetween($scope.viewStartDateTime, $scope.viewEndDateTime, '[]') ||
      // end is within the period
      endMoment.isBetween($scope.viewStartDateTime, $scope.viewEndDateTime, '[]') ||
      // start is before the period, end is after the period
      (startMoment.isBefore($scope.viewStartDateTime) && endMoment.isAfter($scope.viewEndDateTime))
    ) {

      // Get the day details for when this unavailability starts. Note that
      // this only returns days up until the end of the current roster
      // window, e.g. if an unavailability starts on a Saturday
      // and runs until Tuesday, and the company start day is a Monday,
      // the roster for the week this unavailability starts would show it
      // on Saturday, Sunday only. The following weeks roster then shows
      // the Monday and Tuesday portion.
      const allDayDetails = getAllDayDetailsWithinRange(startMoment, endMoment);

      const midnightText = '12:00am';

      const startDate = startMoment.format('YYYY-MM-DD');
      const endDate = endMoment.format('YYYY-MM-DD');

      allDayDetails.forEach(dayDetails => {
        let dayStartTime = startMoment.format('h:mma');
        let dayEndTime = endMoment.format('h:mma');

        // Is this the first day in a multi-day unavailability?
        // If so then the start time stays as-is, but the end time needs
        // to be updated to midnight.
        if (dayDetails.startDate == startDate && endDate != startDate) {
          dayEndTime = midnightText;
        }

        // Is this the last day in a multi-day unavailability?
        // If so then the end time stays as-is, but the start time needs
        // to be updated to midnight.
        if (dayDetails.startDate == endDate && endDate != startDate) {
          dayStartTime = midnightText;
        }

        // If this is a multi-day unavailability, and the current
        // day is neither the start or end day, then the start and end
        // times should both be midnight.
        if (dayDetails.startDate != startDate && dayDetails.startDate != endDate) {
          dayStartTime = midnightText;
          dayEndTime = midnightText;
        }

        // Finally, add the unavailability to the right staff member / day cell.
        $scope.rosterCells[staffMemberId][dayDetails.dayIndex][propertyName].push({
          id: id,
          type: type,
          reason: reason,
          status: status,
          startTime: dayStartTime,
          endTime: dayEndTime,
        });
      })
    }
  }




  var updateView = function() {

    // Establish a list of cells to display on the weekview, each staff member
    // has one for each day of the week, and it'll contain a list of shifts
    // they are rostered for on that day.
    $scope.rosterCells = {};
    angular.forEach($scope.staff, function(member) {
      $scope.rosterCells[member.id] = [];

      angular.forEach($scope.days, function() {
        $scope.rosterCells[member.id].push({
          shifts: [],
          unavailability: [],
          leaveRequests: [],
        })
      });
    });

    // Build the list of unavailability record blocks to be displayed on the
    // weekview UI.
    $scope.unavailability.forEach(record => {

      // Check if the unavailability record belongs to a deactivated staff member
      // If the staff member is deactivated, their unavailability should not be displayed
      if (!$scope.unavailabilityVisible || !$scope.rosterCells[record.staffMemberId]) {
        return;
      }

      // One off unavailability, we use the date on the record and the time
      // directly
      if (record.type == 'OneOff') {
        record.times.forEach(time => {

          // Moments are by default in the company timezone, so we can construct
          // these moments and compare them with other zoned moments safely.
          const startMoment = moment([record.from, 'T', time.from].join(''));
          const endMoment = moment([record.to, 'T', time.to].join(''));

          setRecordTimeBoundaries(
            startMoment,
            endMoment,
            time.id,
            record.type,
            time.reason,
            record.status,
            record.staffMemberId,
            "unavailability"
          );
        })
      }

      // Weekly unavailability, we use the day name on the time record and create
      // entries for each aligned to the correct day index.
      if (record.type == 'Weekly') {

        // Check the record is in scope for the current week view (there can be
        // future changes to unavailability that aren't yet applicable returned).
        // We also need to cater for a record that starts or finishes during the
        // current week view, in this case we need to pick the days before the
        // change and after the change from the correct record that was applicable
        // on the given day...

        const startDate = moment(record.from);
        const endDate = moment(record.to);

        if (startDate.isAfter($scope.viewEndDateTime)) {
          // No days needed to be processed for this weekly record, it's for a
          // future roster period.
          logger.info('Not including any weekly unavailability from %o as it is for a future roster period', record);
          record.times = [];
        }

        const endWithinViewWindow = endDate.isBetween($scope.viewStartDateTime, $scope.viewEndDateTime, '[)');

        if (endWithinViewWindow) {
          // Get the day index for the end date, all days before that index should
          // be included in the unavailability.
          const endDayDetail = getDayDetailByName(endDate.format('dddd'));

          // Remove any times that are at of after the end date
          const validTimes = $scope.isTemplate ? record.times : record.times.filter(time => {
            const dayDetail = getDayDetailByName(time.day);
            // Want <= here as the day that the unavailability record is valid to is still a day that 
            // it should apply
            return dayDetail && dayDetail.dayIndex <= endDayDetail.dayIndex;
          });

          logger.info('Only including times %o from weekly unavailability record %o as it ends during the current roster period', validTimes, record);

          record.times = validTimes;
        }

        const startsWithinViewWindow = startDate.isBetween($scope.viewStartDateTime, $scope.viewEndDateTime, '(]');

        if (startsWithinViewWindow) {
          // Get the day index for the start date, all days after that index should
          // be included in the unavailability.
          const startDayDetail = getDayDetailByName(startDate.format('dddd'));

          // Remove any times that are at or before the start date
          const validTimes = $scope.isTemplate ? record.times : record.times.filter(time => {
            const dayDetail = getDayDetailByName(time.day);
            return dayDetail && dayDetail.dayIndex >= startDayDetail.dayIndex;
          });

          logger.info('Only including times %o from weekly unavailability record %o as it starts during the current roster period', validTimes, record);

          record.times = validTimes;
        }

        // For each time we've got (corresponding to a day in the week) we add an
        // unavailabilty record. Note that we've trimmed the record.times array
        // down in the cases above to remove any days that aren't applicable for
        // the roster dates we're presenting.
        record.times.forEach(time => {

          const dayDetail = getDayDetailByName(time.day);

          $scope.rosterCells[record.staffMemberId][dayDetail.dayIndex].unavailability.push({
            id: time.id,
            type: record.type,
            reason: time.reason,
            status: record.status,
            startTime: moment([dayDetail.startDate, 'T', time.from].join('')).format('h:mma'),
            endTime: moment([dayDetail.startDate, 'T', time.to].join('')).format('h:mma'),
          });

        })
      }
    });

    // Build the list of leave blocks to be displayed on the
    // weekview UI.
    $scope.leaveRequests.forEach(leave => {

      // Check if the leave belongs to a deactivated staff member
      // If the staff member is deactivated, their leave should not be displayed
      if (!$scope.leaveRequestsVisible || !$scope.rosterCells[leave.staffMemberId]) {
        return;
      } 

      // Moments are by default in the company timezone, so we can construct
      // these moments and compare them with other zoned moments safely.
      const startMoment = moment(leave.startDateTime);
      const endMoment = moment(leave.endDateTime);

      setRecordTimeBoundaries(
        startMoment, 
        endMoment, 
        leave.id, 
        leave.type, 
        leave.reason, 
        leave.status, 
        leave.staffMemberId, 
        'leaveRequests'
      );
    });

    angular.forEach($scope.rosteredShifts, function(rosteredShift) {
      var dayIndex = getDayIndex(moment(rosteredShift.clockinTime));
      if (dayIndex === null) {
        return;
      }

      $scope.rosterCells[rosteredShift.staffMemberId][dayIndex].shifts.push(rosteredShift);

      $scope.rosterCells[rosteredShift.staffMemberId][dayIndex].shifts = $filter('orderBy')(
        $scope.rosterCells[rosteredShift.staffMemberId][dayIndex].shifts,
        function(shift) {
          return moment(shift.clockinTime).unix();
        }
      );
    });
  };


  updateView();

  $scope.$watch('[unavailabilityVisible, leaveRequestsVisible]', function() {
    updateView();
  });

  $scope.addShift = function(newShift) {
    $scope.rosterCells[newShift.staffMemberId][getDayIndex(moment(newShift.clockinTime))].shifts.push(newShift);

    $scope.baseAddShift(newShift)
    .then(function() {
      updateView();
    });
  };

  $scope.updateShift = function(updatedShift) {
    $scope.baseUpdateShift(updatedShift)
    .then(function() {
      updateView();
    });
  };

  $scope.setSort = function(newSortCol) {

    if (newSortCol === 'name') {

      // Simple alphabetical sort based on staff member name.
      $scope.staff
        .sort(function(a, b) {
          return a.name < b.name ? -1 : 0;
        });

    } else {

      var withShifts = [];
      var withoutShifts = [];

      // Gather two sets, those staff with shifts and those without.
      angular.forEach($scope.staff, function(staffMember) {
        if ($scope.rosterCells[staffMember.id][newSortCol].shifts.length > 0) {
          withShifts.push(staffMember);
        } else {
          withoutShifts.push(staffMember);
        }
      });

      // Sort the staff members with shifts into order based on the chosen time
      // the user want's to order by.
      //
      // We sort by the start time of the shift for all staff members that have
      // a shift that finishes after the selected time, those staff members that
      // only have an earlier shift get alphabetically ordered below the staff
      // members with shifts that are within the desired range.
      withShifts
        .sort(function(a, b) {

          // Find the first shift for the staff members we're comparing that
          // starts after the selected order by time.
          var aShift = $scope.rosterCells[a.id][newSortCol].shifts.find(function(shift) {
            return moment(shift.clockoutTime);
          });

          var bShift = $scope.rosterCells[b.id][newSortCol].shifts.find(function(shift) {
            return moment(shift.clockoutTime);
          });

          // If the staff member only has a shift that finishes before the
          // sorting column time then we order alphabetically based on their
          // name. Due to how string comparasion works a name will sort after
          // a datetime, meaning the staff members without shifts in the selected
          // order by time period will go after all staff members with shifts,
          // and they'll be alphabetically ordered too...
          var aClockinTime = aShift ? aShift.clockinTime : a.name;
          var bClockinTime = bShift ? bShift.clockinTime : b.name;

          if (aClockinTime < bClockinTime) {
            return -1;
          }

          if (aClockinTime === bClockinTime) {
            return a.name < b.name ? -1 : 0;
          }

          return 1;
        });

      withoutShifts
        .sort(function(a, b) {
          return a.name < b.name ? -1 : 0;
        });

      $scope.staff = withShifts.concat(withoutShifts);

    }

  };

  $scope.openEditDialog = function(data) {
    $scope.showShiftEditorDialog(data)
    .then(function(updatedShifts) {
      angular.forEach(updatedShifts, function(updatedShift) {
        if (updatedShift.deleted) {
          $scope.baseDeleteShift(updatedShift)
          .then(function() {
            updateView();
          });
        } else if (updatedShift.id) {
          $scope.updateShift(updatedShift);
        } else {
          updatedShift.rosterId = $scope.templateId;
          $scope.addShift(updatedShift);
        }
      });
    });
  };

  $scope.staffView = function(staffMember) {
    dialog.open({
      templateUrl: 'modules/time/roster/staffview.tpl.html',
      controller: 'TimeRosterStaffviewCtrl',
      size: 'lg',
      scope: $scope,
      resolve: {
        staffMemberDayData: function() {
          return $scope.rosterCells[staffMember.id];
        },
        staff: function() {
          return staffMember;
        }
      }
    });
  };

  $scope.reassignShifts = function(staffMember) {
    dialog.open({
      templateUrl: 'modules/time/roster/reassignShifts.tpl.html',
      controller: 'TimeRosterReassignShiftsCtrl',
      size: 'md',
      scope: $scope,
      resolve: {
        staffMember: staffMember,
        isTemplate: $scope.isTemplate
      }
    });
  };
}]);